import { ExportButton, FilterDropdown, SearchDropdown } from "admin/ui/base";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { UsersTable, UsersTableProps } from "../UserAdmin/UsersTable";
import { IPaginateRequest, IUser, IUserStatus, IUserStatusCounts } from "utils/types";
import { IProfessionalOptionKey, ISubscriptionOptionKey, IUserStatusOptionKey, professionOptions, searchOptions, subscriptionOptions, userStatusOptions } from "./data";
import { getPaginationLimitSkip, useTablePagination } from "hooks/utils/usePagination";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  gap: 24px;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Title = styled.h1`
  font-family: Manrope-Medium;
  font-size: 24px;
  line-height: 32px;
  color: #333;
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const SelectContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const SearchContainer = styled.div`
  width: 600px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  width: 100%;
  font-family: "Manrope", sans-serif;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.05);
`;

const StatLabel = styled.p`
  font-family: "Manrope", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #666;
  margin: 0 0 8px 0;
`;

const StatValue = styled.p`
  font-family: Manrope-Medium;
  font-size: 28px;
  line-height: 36px;
  color: #333;
  margin: 0;
`;

const TableContainer = styled.div`
  width: 100%;
`;


export interface UsersProps extends Pick<UsersTableProps, "onViewUser">{
  onGetUsersStat: () => Promise<IUserStatusCounts|undefined>;
  onChangeUserStatus: (user: IUser, status: IUserStatus) => Promise<IUser|undefined>;
  onGetUsers: (data: {search?:string; user?: Partial<IUser>} & IPaginateRequest) => Promise<{data: IUser[]; total: number}>
  onDeleteUser: (userId: string) => Promise<string|undefined>;
}

export const Users: React.FC<UsersProps> = ({
  onGetUsersStat, onGetUsers, onChangeUserStatus, onViewUser,
  onDeleteUser
}) => {
  const [stat, setStat] = useState<IUserStatusCounts>()
  const [filter, setFilter] = useState<{
    status: IUserStatusOptionKey; 
    profession: IProfessionalOptionKey; 
    premium: ISubscriptionOptionKey;
    search: string
  }>({
    status: "all", profession: "all", premium: "all",
    search: ""
  })

  const getUsers = useCallback((pagination: IPaginateRequest) => {
      const { status, profession, premium, search } = filter;
  
      const user:Partial<IUser> = {}
      if(status !== "all") {
        user.userStatus = status
      }
      if(profession !== "all") {
        user.profession = profession
      }
      if(premium != "all"){
        user.onActivePremium = "true"
      }
      return onGetUsers({user, search, ...pagination})
    }, [JSON.stringify(filter)])
  
    const { data: users, total, page, pageSize, onChange, setData} = useTablePagination({
      deps: [JSON.stringify(filter)],
      fetcher(params) {
        return getUsers(getPaginationLimitSkip(params))
      },
    })
  useEffect(() => {
    onGetUsersStat().then(setStat)
  }, [])

  return (
    <Container>
      <Header>
        <Title>Users</Title>
        <ExportButton />
      </Header>

      <StatsContainer>
        <StatCard>
          <StatLabel>Total users</StatLabel>
          <StatValue>{stat?.total || 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Premium users</StatLabel>
          <StatValue>{0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Active users</StatLabel>
          <StatValue>{stat?.active || 0}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Suspended users</StatLabel>
          <StatValue>{stat?.suspended || 0}</StatValue>
        </StatCard>
       {/*  <StatCard>
          <StatLabel>Website visits in last 24h</StatLabel>
          <StatValue>{stat?.websiteVisits}</StatValue>
        </StatCard> */}
      </StatsContainer>

      <FiltersContainer>
        <SelectContainer>
          <FilterDropdown<IUserStatusOptionKey>
            options={userStatusOptions}
            placeholder="User status"
            defaultValue="User status"
            onChange={(value) => setFilter({ ...filter, status: value })}
          />
          <FilterDropdown<IProfessionalOptionKey>
            options={professionOptions}
            placeholder="Profession"
            defaultValue="Profession"
            onChange={(value) => setFilter({ ...filter, profession: value })}
          />
          <FilterDropdown<ISubscriptionOptionKey>
            options={subscriptionOptions}
            placeholder="Subscription"
            defaultValue="Subscription"
            onChange={(value) => setFilter({ ...filter, premium: value })}
          />
        </SelectContainer>
        <SearchContainer>
          <SearchDropdown
            options={[]}
            placeholder="Search for users"
            {...{
              onChange(value) {
                setFilter(data => ({...data, search: value as string}))
              },
            }}
          />
        </SearchContainer>
      </FiltersContainer>

      <TableContainer>
        <UsersTable {...{
          isAdmin: false,
          users, 
          onViewUser,
          pagination: {
            current: page,
            pageSize,
            total,
            onChange,
          },
          async onDeleteUser(user) {
            const res = await onDeleteUser(user.id)
            if(res){
              setData(users.filter((u) => u.id !== user.id))
              onGetUsersStat().then(setStat)
            }
          },
          async onChangeUserStatus(user, status) {
            const res = await onChangeUserStatus(user, status)
            if(res){
              setData(users.map((u) => u.id === user.id ? res : u))
              onGetUsersStat().then(setStat)
            }
          }, 
        }}/>
      </TableContainer>
    </Container>
  );
};
